/*
 * ALOHA QUEEN SUPERNOVA - Forgot Password
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - NO UNAUTHORIZED USE
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password reset link sent! Check your email.');
      } else {
        setError(data.detail || 'Failed to send reset link');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <RealisticGalaxyBackground />
      
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-gray-900/80 backdrop-blur-xl rounded-2xl p-8 border border-purple-500/30 shadow-2xl">
          <Link to="/login" className="flex items-center text-gray-300 hover:text-white mb-6 transition">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Login
          </Link>
          
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Forgot Password?</h2>
            <p className="text-gray-300">No worries! Enter your email and we'll send you a reset link.</p>
          </div>

          {message && (
            <div className="bg-green-500/20 border border-green-500 text-green-300 px-4 py-3 rounded-lg mb-6">
              {message}
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-300 mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-3 bg-gray-800/50 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <div className="text-center mt-6 text-gray-400 text-sm">
            Remember your password?{' '}
            <Link to="/login" className="text-purple-400 hover:text-purple-300 font-semibold">
              Login here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
